import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { editWorkshopRegister, getSingleWorkshopRegister } from "../../redux/services/workshopRegisterSlice";
import { ROUTES } from "../../constant";

const WorkshopRegisterForm = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const singleWorkshop = useSelector((state) => state.workshopRegister.singleWorkshopRegister);

  const queryParams = new URLSearchParams(location.search);
  const workshopRegisterId = queryParams.get("workshopId");

  const [loading, setLoading] = useState(false);

  const initialValues = {
    workshopRegisterId: "",
    name: "",
    phone: "",
    city: "",
    // slotDate: "",
    paymentDate: "",
    email: "",
    merchantId: "",
    paymentId: "",
    referralCode: "",
    reachFrom: "",
    status: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    phone: Yup.string().required("Phone is required"),
    city: Yup.string().required("City is required"),
    // slotDate: Yup.string().required("Slot Date is required"),
    paymentDate: Yup.string().required("Payment Date is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    merchantId: Yup.string().required("Merchant ID is required"),
    paymentId: Yup.string().required("Payment ID is required"),
    referralCode: Yup.string().nullable(),
    reachFrom: Yup.string().nullable(),
    status: Yup.string().required("Status is required"),
  });

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      if (workshopRegisterId) values.workshopRegisterId = workshopRegisterId;

      const response = await dispatch(editWorkshopRegister(values));

      if (response?.payload?.status === 200) {
        toast.success(response?.payload?.message, {
          autoClose: 2000,
          pauseOnHover: false,
        });
        navigate(ROUTES.WORKSHOPS.BASE);
      }
    } catch (error) {
      console.error("Error saving workshop register:", error);
    } finally {
      setLoading(false);
    }
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setValues } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const getSingleRecord = useCallback(async () => {
    try {
      await dispatch(getSingleWorkshopRegister(workshopRegisterId));
    } catch (error) {
      console.error("Error fetching single workshop register:", error);
    }
  }, [dispatch, workshopRegisterId]);

  useEffect(() => {
    if (workshopRegisterId) getSingleRecord();
  }, [workshopRegisterId, getSingleRecord]);

  useEffect(() => {
    if (workshopRegisterId && singleWorkshop) {
      setValues({
        workshopRegisterId: singleWorkshop?._id ?? "",
        name: singleWorkshop?.name ?? "",
        phone: singleWorkshop?.phone ?? "",
        city: singleWorkshop?.city ?? "",
        // slotDate: singleWorkshop?.slotDate ?? "",
        paymentDate: singleWorkshop?.paymentDate ?? "",
        email: singleWorkshop?.email ?? "",
        merchantId: singleWorkshop?.merchantId ?? "",
        paymentId: singleWorkshop?.paymentId ?? "",
        referralCode: singleWorkshop?.referralCode ?? "",
        reachFrom: singleWorkshop?.reachFrom ?? "",
        status: singleWorkshop?.status ?? "",
      });
    }
  }, [workshopRegisterId, singleWorkshop, setValues]);

  return (
    <div>
      <div className="bg-background py-4 sm:py-4">
        <div className="px-4 md:py-10 py-5 bg-card shadow rounded-3xl sm:p-10">
          <p className="md:text-3xl text-xl text-center font-medium uppercase text-primary">{action} Workshop Register</p>

          <form className="mt-6" onSubmit={handleSubmit}>
            <div className="grid md:grid-cols-2 grid-cols-1 gap-5">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-foreground">Name</label>
                <input type="text" name="name" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.name} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Name" />
                {touched.name && errors.name && <div className="text-red-500 text-sm">{errors.name}</div>}
              </div>

              {/* Phone */}
              <div>
                <label className="text-sm font-medium text-foreground">Phone</label>
                <input type="text" name="phone" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.phone} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Phone" />
                {touched.phone && errors.phone && <div className="text-red-500 text-sm">{errors.phone}</div>}
              </div>

              {/* City */}
              <div>
                <label className="text-sm font-medium text-foreground">City</label>
                <input type="text" name="city" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.city} onChange={handleChange} onBlur={handleBlur} placeholder="Enter City" />
                {touched.city && errors.city && <div className="text-red-500 text-sm">{errors.city}</div>}
              </div>

              {/* Slot Date */}
              {/* <div>
                <label className="text-sm font-medium text-foreground">Slot Date</label>
                <input type="date" name="slotDate" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.slotDate} onChange={handleChange} onBlur={handleBlur} />
                {touched.slotDate && errors.slotDate && <div className="text-red-500 text-sm">{errors.slotDate}</div>}
              </div> */}

              {/* Payment Date */}
              <div>
                <label className="text-sm font-medium text-foreground">Payment Date</label>
                <input type="date" name="paymentDate" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.paymentDate} onChange={handleChange} onBlur={handleBlur} />
                {touched.paymentDate && errors.paymentDate && <div className="text-red-500 text-sm">{errors.paymentDate}</div>}
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-foreground">Email</label>
                <input type="email" name="email" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.email} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Email" />
                {touched.email && errors.email && <div className="text-red-500 text-sm">{errors.email}</div>}
              </div>

              {/* Merchant ID */}
              <div>
                <label className="text-sm font-medium text-foreground">Merchant ID</label>
                <input type="text" name="merchantId" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.merchantId} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Merchant ID" />
                {touched.merchantId && errors.merchantId && <div className="text-red-500 text-sm">{errors.merchantId}</div>}
              </div>

              {/* Payment ID */}
              <div>
                <label className="text-sm font-medium text-foreground">Payment ID</label>
                <input type="text" name="paymentId" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.paymentId} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Payment ID" />
                {touched.paymentId && errors.paymentId && <div className="text-red-500 text-sm">{errors.paymentId}</div>}
              </div>

              {/* Referral Code */}
              <div>
                <label className="text-sm font-medium text-foreground">Referral Code</label>
                <input type="text" name="referralCode" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.referralCode} onChange={handleChange} onBlur={handleBlur} placeholder="Enter Referral Code" />
              </div>

              {/* Reach From */}
              <div>
                <label className="text-sm font-medium text-foreground">Reach From</label>
                <input type="text" name="reachFrom" className="w-full px-4 py-2 border border-border rounded-xl bg-card" value={values.reachFrom} onChange={handleChange} onBlur={handleBlur} placeholder="Where did you hear about us?" />
              </div>

              {/* Status */}
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-foreground">Status</label>
                <select name="status" className="w-full px-4 py-2 border border-border rounded-xl" value={values.status} onChange={handleChange}>
                  {["PENDING", "COMPLETED", "FAILED"]?.map((s, i) => (
                    <option key={i} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {touched.status && errors.status && <div className="text-red-500 text-sm">{errors.status}</div>}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <button type="submit" className="w-full sm:w-[250px] min-h-[50px] flex justify-center items-center text-white bg-primary text-lg font-semibold rounded-xl hover:bg-accent mt-7">
                {loading ? <div className="loader"></div> : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WorkshopRegisterForm;
